import { Skeleton } from "@/components/ui/skeleton";
import FieldInput from "@/dashboard/components/fundings/FieldInput";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import { cn } from "@/libs/utils";
import {
  AlertTriangle,
  BriefcaseBusiness,
  CalendarDays,
  ChevronRight,
  ExternalLink,
  FolderKanban,
  Search,
  Wallet,
} from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  SynthesesCalendarEvent,
  SynthesesReminderItem,
} from "./SynthesesRightRail";
import SynthesesProjectImpactIndicators from "./SynthesesProjectImpactIndicators";
import {
  getSynthesesMetricToneClasses,
  type SynthesesMetricTone,
} from "./synthesesMetricTones";
import useSynthesesGroupedContractsRead, {
  SynthesesGroupedContractNode,
  SynthesesGroupedFinanceurItem,
} from "./useSynthesesGroupedContractsRead";

type ProgramResponse = Array<{
  id?: number;
  name?: string;
  projects?: Array<{
    id?: number;
    name?: string;
  }>;
}>;

type ProjectCatalogItem = {
  id: number;
  name: string;
  programName: string;
};

type ProjectMonthlyBucket = {
  contractAmountEuro: number;
  expectedPaymentsEuro: number;
  receivedPaymentsEuro: number;
  landPaymentsEuro: number;
  landPaymentsPlannedEuro: number;
  landPaymentsRealizedEuro: number;
  landPaymentsCertifiedEuro: number;
};

type ProjectContractEvent = {
  id: string;
  date: string | null;
  amountEuro: number;
  status?: number | null;
  datePaid?: string | null;
};

type RawProjectPaymentRow = {
  id?: number | string | null;
  at?: string | null;
  dateMeasure?: string | null;
  dateVerified?: string | null;
  amountEuro?: number | string | null;
  status?: number | string | null;
  landPaymentStatus?: number | string | null;
  datePaid?: string | null;
};

type ProjectContractEntry = {
  id: string;
  title: string;
  financeurName: string;
  contractDate: string | null;
  dateUsesFallback: boolean;
  isVerdeo: boolean;
  sourceType: string;
  projectAmountEuro: number;
  yearAllocations: Record<string, number>;
  expectedPayments: ProjectContractEvent[];
  paidPayments: ProjectContractEvent[];
  landPayments: ProjectContractEvent[];
};

type ProjectYearSummary = {
  year: string;
  contractAmountEuro: number;
  expectedPaymentsEuro: number;
  receivedPaymentsEuro: number;
  landPaymentsEuro: number;
  landPaymentsPlannedEuro: number;
  landPaymentsRealizedEuro: number;
  landPaymentsCertifiedEuro: number;
  months: Record<number, ProjectMonthlyBucket>;
  contractCount: number;
};

type ProjectNode = {
  id: number;
  name: string;
  programName: string;
  contractCount: number;
  funderCount: number;
  totalAmountEuro: number;
  expectedPaymentsEuro: number;
  receivedPaymentsEuro: number;
  landPaymentsEuro: number;
  landPaymentsPlannedEuro: number;
  landPaymentsRealizedEuro: number;
  landPaymentsCertifiedEuro: number;
  contracts: ProjectContractEntry[];
  years: ProjectYearSummary[];
};

const MONTH_COLUMNS: Array<{ index: number; label: string }> = [
  { index: 0, label: "Jan" },
  { index: 1, label: "Fev" },
  { index: 2, label: "Mar" },
  { index: 3, label: "Avr" },
  { index: 4, label: "Mai" },
  { index: 5, label: "Juin" },
  { index: 6, label: "Juil" },
  { index: 7, label: "Aou" },
  { index: 8, label: "Sep" },
  { index: 9, label: "Oct" },
  { index: 10, label: "Nov" },
  { index: 11, label: "Dec" },
];

const MONTH_LABEL_COLUMN_CLASS = "w-[170px] shrink-0";
const MONTH_CELLS_ROW_CLASS =
  "flex flex-nowrap gap-2 [&>div]:!w-[74px] [&>div]:!min-w-[74px] [&>div]:!max-w-[74px] [&>div]:!flex-none";

const LAND_PAYMENT_STATUS_PLANNED = 1;
const LAND_PAYMENT_STATUS_REALIZED = 2;
const LAND_PAYMENT_STATUS_CERTIFIED = 3;

function toTrimmedText(value: unknown) {
  return String(value ?? "").trim();
}

function formatEuro(value: number) {
  return `${Number(value || 0).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;
}

function formatDate(value: string | null) {
  if (!value) return "Date du contrat non renseignee";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date invalide";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

function getProjectDashboardHref(projectId: number | null | undefined) {
  const normalizedId = Number(projectId || 0);
  if (!Number.isFinite(normalizedId) || normalizedId <= 0) return null;
  return `/dashboard/project/${normalizedId}`;
}

function getScrollParent(node: HTMLElement | null): HTMLElement | Window {
  if (!node || typeof window === "undefined") return window;

  let parent: HTMLElement | null = node.parentElement;
  while (parent) {
    const style = window.getComputedStyle(parent);
    const overflowY = style.overflowY;
    if (overflowY === "auto" || overflowY === "scroll") {
      return parent;
    }
    parent = parent.parentElement;
  }

  return window;
}

function scrollToTargetNode(targetNodeId: string) {
  const target = Array.from(
    document.querySelectorAll<HTMLElement>("[data-contract-node-id]"),
  ).find((node) => node.dataset.contractNodeId === targetNodeId);

  if (!target) return;

  const stickyHeader = document.querySelector<HTMLElement>(
    "[data-syntheses-sticky-header='true']",
  );
  const stickyHeaderHeight = stickyHeader
    ? stickyHeader.getBoundingClientRect().height
    : 0;
  target.style.scrollMarginTop = `${stickyHeaderHeight + (stickyHeaderHeight > 0 ? 24 : 16)}px`;
  target.scrollIntoView({
    behavior: "smooth",
    block: "start",
  });
}

function toProjectIdKey(projectId: number | null, fallbackName: string) {
  return projectId && projectId > 0 ? `project:${projectId}` : `name:${fallbackName.toLowerCase()}`;
}

function extractYear(value: string | null | undefined): string | null {
  const raw = toTrimmedText(value);
  if (!raw) return null;
  const date = new Date(raw);
  if (!Number.isNaN(date.getTime())) {
    return String(date.getFullYear());
  }
  const match = raw.match(/^(\d{4})/);
  return match?.[1] ?? null;
}

function extractMonthIndex(value: string | null | undefined): number | null {
  const raw = toTrimmedText(value);
  if (!raw) return null;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return null;
  return date.getMonth();
}

function getLandPaymentStatus(value: unknown) {
  const status = Number(value || 0);
  return Number.isFinite(status) ? status : 0;
}

function getLandPaymentDate(row: RawProjectPaymentRow, status: number) {
  const plannedDate = toTrimmedText(row?.at);
  const realizedDate = toTrimmedText(row?.dateMeasure);
  const certifiedDate = toTrimmedText(row?.dateVerified);

  if (status === LAND_PAYMENT_STATUS_CERTIFIED) {
    return certifiedDate || realizedDate || plannedDate || null;
  }

  if (status === LAND_PAYMENT_STATUS_REALIZED) {
    return realizedDate || plannedDate || null;
  }

  return plannedDate || realizedDate || certifiedDate || null;
}

function getLandPaymentStatusLabel(status: number) {
  if (status === LAND_PAYMENT_STATUS_PLANNED) return "Versement terrain prévu";
  if (status === LAND_PAYMENT_STATUS_REALIZED) return "Versement terrain réalisé";
  if (status === LAND_PAYMENT_STATUS_CERTIFIED) return "Versement terrain certifié";
  return "Versement terrain";
}

type LandPaymentTotalsTarget = {
  landPaymentsEuro: number;
  landPaymentsPlannedEuro: number;
  landPaymentsRealizedEuro: number;
  landPaymentsCertifiedEuro: number;
};

function addLandPaymentAmountByStatus(
  target: LandPaymentTotalsTarget,
  status: number,
  amountEuro: number,
) {
  if (status === LAND_PAYMENT_STATUS_PLANNED) {
    target.landPaymentsPlannedEuro += amountEuro;
    return;
  }

  if (status === LAND_PAYMENT_STATUS_REALIZED) {
    target.landPaymentsRealizedEuro += amountEuro;
    target.landPaymentsEuro += amountEuro;
    return;
  }

  if (status === LAND_PAYMENT_STATUS_CERTIFIED) {
    target.landPaymentsCertifiedEuro += amountEuro;
  }
}

function sumLandPaymentsByStatus(
  contracts: ProjectContractEntry[],
  status: number,
) {
  return Number(
    contracts.reduce(
      (sum, contract) =>
        sum + contract.landPayments.reduce(
          (eventSum, event) =>
            getLandPaymentStatus(event.status) === status
              ? eventSum + Number(event.amountEuro || 0)
              : eventSum,
          0,
        ),
      0,
    ).toFixed(2),
  );
}

function createMonthlyBucket(): ProjectMonthlyBucket {
  return {
    contractAmountEuro: 0,
    expectedPaymentsEuro: 0,
    receivedPaymentsEuro: 0,
    landPaymentsEuro: 0,
    landPaymentsPlannedEuro: 0,
    landPaymentsRealizedEuro: 0,
    landPaymentsCertifiedEuro: 0,
  };
}

function createYearSummary(year: string): ProjectYearSummary {
  return {
    year,
    contractAmountEuro: 0,
    expectedPaymentsEuro: 0,
    receivedPaymentsEuro: 0,
    landPaymentsEuro: 0,
    landPaymentsPlannedEuro: 0,
    landPaymentsRealizedEuro: 0,
    landPaymentsCertifiedEuro: 0,
    months: {},
    contractCount: 0,
  };
}

export function buildProjectCatalog(programsResponse: ProgramResponse | null | undefined) {
  const catalog = new Map<number, ProjectCatalogItem>();
  const programs = Array.isArray(programsResponse) ? programsResponse : [];

  programs.forEach((program) => {
    const programName = toTrimmedText(program?.name);
    const projects = Array.isArray(program?.projects) ? program.projects : [];
    projects.forEach((project) => {
      const projectId = Number(project?.id);
      if (!Number.isFinite(projectId) || projectId <= 0 || catalog.has(projectId)) return;
      catalog.set(projectId, {
        id: projectId,
        name: toTrimmedText(project?.name) || `Projet #${projectId}`,
        programName,
      });
    });
  });

  return Array.from(catalog.values()).sort((a, b) => a.name.localeCompare(b.name, "fr"));
}

function ensureProjectNode(
  map: Map<string, ProjectNode>,
  projectId: number,
  projectName: string,
  programName: string,
) {
  const key = toProjectIdKey(projectId, projectName);
  if (!map.has(key)) {
    map.set(key, {
      id: projectId,
      name: projectName,
      programName,
      contractCount: 0,
      funderCount: 0,
      totalAmountEuro: 0,
      expectedPaymentsEuro: 0,
      receivedPaymentsEuro: 0,
      landPaymentsEuro: 0,
      landPaymentsPlannedEuro: 0,
      landPaymentsRealizedEuro: 0,
      landPaymentsCertifiedEuro: 0,
      contracts: [],
      years: [],
    });
  }
  return map.get(key)!;
}

function resolveContractProjectAllocations(contract: SynthesesGroupedContractNode) {
  const allocations = new Map<
    string,
    {
      projectId: number;
      projectName: string;
      programName: string;
      totalAmountEuro: number;
      yearAllocations: Record<string, number>;
    }
  >();

  const lineRows = Array.isArray(contract.lineRows) ? contract.lineRows : [];
  lineRows.forEach((lineRow) => {
    const projectId = Number(lineRow?.projectId || 0);
    const projectName = toTrimmedText(lineRow?.projectName) || toTrimmedText(contract.project?.name);
    if ((!projectId || projectId <= 0) && !projectName) return;
    const key = toProjectIdKey(projectId || 0, projectName || "projet");
    const year = String(Number(lineRow?.year || 0) || "").trim() || extractYear(contract.contractDate) || "Sans date";
    const amountEuro = Number(lineRow?.amountEuro || 0);

    if (!allocations.has(key)) {
      allocations.set(key, {
        projectId: projectId || 0,
        projectName: projectName || `Projet #${projectId}`,
        programName: toTrimmedText(contract.project?.program?.name),
        totalAmountEuro: 0,
        yearAllocations: {},
      });
    }

    const entry = allocations.get(key)!;
    entry.totalAmountEuro += Number.isFinite(amountEuro) ? amountEuro : 0;
    entry.yearAllocations[year] = Number(
      ((entry.yearAllocations[year] || 0) + (Number.isFinite(amountEuro) ? amountEuro : 0)).toFixed(2),
    );
  });

  if (allocations.size === 0) {
    const projectId = Number(contract.project?.id || 0);
    const projectName = toTrimmedText(contract.project?.name);
    if (projectId > 0 || projectName) {
      const key = toProjectIdKey(projectId || 0, projectName || "projet");
      const fallbackYear =
        (Array.isArray(contract.years) && contract.years.length > 0
          ? String(Number(contract.years[0] || 0) || "")
          : extractYear(contract.contractDate)) || "Sans date";
      allocations.set(key, {
        projectId: projectId || 0,
        projectName: projectName || `Projet #${projectId}`,
        programName: toTrimmedText(contract.project?.program?.name),
        totalAmountEuro: Number(contract.amountEuro || 0),
        yearAllocations: {
          [fallbackYear]: Number(contract.amountEuro || 0),
        },
      });
    }
  }

  return Array.from(allocations.values());
}

function mapScaledEvents(
  rows: RawProjectPaymentRow[],
  ratio: number,
  kind: "expected" | "paid" | "land",
  contractId: string,
) {
  return (Array.isArray(rows) ? rows : []).map((row, index) => {
    const landStatus = getLandPaymentStatus(row?.status ?? row?.landPaymentStatus);

    return {
      id: `${contractId}-${kind}-${row?.id ?? index}`,
      date:
        kind === "land"
          ? getLandPaymentDate(row, landStatus)
          : (toTrimmedText(row?.at) || null),
      amountEuro: Number((Number(row?.amountEuro || 0) * ratio).toFixed(2)),
      status:
        kind === "land"
          ? landStatus || null
          : (Number.isFinite(Number(row?.status)) ? Number(row?.status) : null),
      datePaid: toTrimmedText(row?.datePaid) || null,
    };
  });
}

export function buildProjectsFromContracts(
  catalog: ProjectCatalogItem[],
  financeurs: SynthesesGroupedFinanceurItem[],
) {
  const map = new Map<string, ProjectNode>();

  catalog.forEach((project) => {
    ensureProjectNode(map, project.id, project.name, project.programName);
  });

  financeurs.forEach((financeur, financeurIndex) => {
    financeur.contracts.forEach((contract, contractIndex) => {
      const projectAllocations = resolveContractProjectAllocations(contract);
      if (!projectAllocations.length) return;

      const allocationBaseAmountEuro = Number(
        projectAllocations.reduce((sum, item) => sum + Number(item.totalAmountEuro || 0), 0).toFixed(2),
      );
      const fallbackBaseAmountEuro = Number(contract.amountEuro || 0);
      const ratioBase = allocationBaseAmountEuro > 0 ? allocationBaseAmountEuro : fallbackBaseAmountEuro;

      projectAllocations.forEach((allocation, allocationIndex) => {
        const projectNode = ensureProjectNode(
          map,
          allocation.projectId,
          allocation.projectName || `Projet #${allocation.projectId}`,
          allocation.programName,
        );
        const projectAmountEuro = Number(Number(allocation.totalAmountEuro || 0).toFixed(2));
        const ratio = ratioBase > 0 ? projectAmountEuro / ratioBase : 1 / projectAllocations.length;
        const contractId = `${contract.id}-project-${allocation.projectId || allocationIndex}`;
        const contractEntry: ProjectContractEntry = {
          id: contractId,
          title:
            toTrimmedText(contract.title) ||
            `Contrat ${contractIndex + 1}`,
          financeurName:
            toTrimmedText(contract.financier?.name) ||
            toTrimmedText(financeur?.name) ||
            `Financeur ${financeurIndex + 1}`,
          contractDate: contract.contractDate,
          dateUsesFallback: contract.dateAmountFallback === true,
          isVerdeo: contract.isVerdeo === true,
          sourceType: toTrimmedText(contract.sourceType),
          projectAmountEuro,
          yearAllocations: allocation.yearAllocations,
          expectedPayments: mapScaledEvents(contract.expectedPayments, ratio, "expected", contractId),
          paidPayments: mapScaledEvents(contract.paidPayments, ratio, "paid", contractId),
          landPayments: mapScaledEvents(contract.landPayments, ratio, "land", contractId),
        };

        projectNode.contracts.push(contractEntry);
      });
    });
  });

  return Array.from(map.values())
    .map((project) => {
      const yearMap = new Map<string, ProjectYearSummary>();
      const funderKeys = new Set<string>();
      const contractKeys = new Set<string>();

      project.contracts.forEach((contract) => {
        funderKeys.add(contract.financeurName.toLowerCase());
        contractKeys.add(contract.id);

        Object.entries(contract.yearAllocations).forEach(([year, amountEuro]) => {
          if (!yearMap.has(year)) {
            yearMap.set(year, createYearSummary(year));
          }
          const yearNode = yearMap.get(year)!;
          yearNode.contractAmountEuro += Number(amountEuro || 0);
        });

        const contractDateYear = extractYear(contract.contractDate);
        const contractMonth = extractMonthIndex(contract.contractDate);
        if (contractDateYear && contractMonth !== null) {
          if (!yearMap.has(contractDateYear)) {
            yearMap.set(contractDateYear, createYearSummary(contractDateYear));
          }
          const yearNode = yearMap.get(contractDateYear)!;
          if (!yearNode.months[contractMonth]) {
            yearNode.months[contractMonth] = createMonthlyBucket();
          }
          const yearAmount = Number(contract.yearAllocations[contractDateYear] || 0);
          yearNode.months[contractMonth].contractAmountEuro += yearAmount > 0 ? yearAmount : contract.projectAmountEuro;
        }

        contract.expectedPayments.forEach((event) => {
          const year = extractYear(event.date) || contractDateYear || "Sans date";
          const monthIndex = extractMonthIndex(event.date);
          if (!yearMap.has(year)) {
            yearMap.set(year, createYearSummary(year));
          }
          const yearNode = yearMap.get(year)!;
          yearNode.expectedPaymentsEuro += event.amountEuro;
          if (monthIndex !== null) {
            if (!yearNode.months[monthIndex]) {
              yearNode.months[monthIndex] = createMonthlyBucket();
            }
            yearNode.months[monthIndex].expectedPaymentsEuro += event.amountEuro;
          }
        });

        contract.paidPayments.forEach((event) => {
          const year = extractYear(event.date) || contractDateYear || "Sans date";
          const monthIndex = extractMonthIndex(event.date);
          if (!yearMap.has(year)) {
            yearMap.set(year, createYearSummary(year));
          }
          const yearNode = yearMap.get(year)!;
          yearNode.receivedPaymentsEuro += event.amountEuro;
          if (monthIndex !== null) {
            if (!yearNode.months[monthIndex]) {
              yearNode.months[monthIndex] = createMonthlyBucket();
            }
            yearNode.months[monthIndex].receivedPaymentsEuro += event.amountEuro;
          }
        });

        contract.landPayments.forEach((event) => {
          const year = extractYear(event.date) || contractDateYear || "Sans date";
          const monthIndex = extractMonthIndex(event.date);
          const status = getLandPaymentStatus(event.status);
          if (!yearMap.has(year)) {
            yearMap.set(year, createYearSummary(year));
          }
          const yearNode = yearMap.get(year)!;
          addLandPaymentAmountByStatus(yearNode, status, event.amountEuro);
          if (monthIndex !== null) {
            if (!yearNode.months[monthIndex]) {
              yearNode.months[monthIndex] = createMonthlyBucket();
            }
            addLandPaymentAmountByStatus(yearNode.months[monthIndex], status, event.amountEuro);
          }
        });
      });

      const years = Array.from(yearMap.values())
        .map((yearNode) => ({
          ...yearNode,
          contractAmountEuro: Number(yearNode.contractAmountEuro.toFixed(2)),
          expectedPaymentsEuro: Number(yearNode.expectedPaymentsEuro.toFixed(2)),
          receivedPaymentsEuro: Number(yearNode.receivedPaymentsEuro.toFixed(2)),
          landPaymentsEuro: Number(yearNode.landPaymentsEuro.toFixed(2)),
          landPaymentsPlannedEuro: Number(yearNode.landPaymentsPlannedEuro.toFixed(2)),
          landPaymentsRealizedEuro: Number(yearNode.landPaymentsRealizedEuro.toFixed(2)),
          landPaymentsCertifiedEuro: Number(yearNode.landPaymentsCertifiedEuro.toFixed(2)),
          contractCount: project.contracts.filter(
            (contract) => Number(contract.yearAllocations[yearNode.year] || 0) > 0,
          ).length,
        }))
        .sort((a, b) => {
          if (a.year === "Sans date") return 1;
          if (b.year === "Sans date") return -1;
          return Number(a.year) - Number(b.year);
        });

      const sortedContracts = [...project.contracts].sort((a, b) => {
        const dateSort = String(a.contractDate || "").localeCompare(String(b.contractDate || ""));
        if (dateSort !== 0) return dateSort;
        return a.title.localeCompare(b.title, "fr");
      });

      return {
        ...project,
        contractCount: contractKeys.size,
        funderCount: funderKeys.size,
        totalAmountEuro: Number(
          sortedContracts.reduce((sum, contract) => sum + Number(contract.projectAmountEuro || 0), 0).toFixed(2),
        ),
        expectedPaymentsEuro: Number(
          sortedContracts.reduce(
            (sum, contract) =>
              sum + contract.expectedPayments.reduce((eventSum, event) => eventSum + Number(event.amountEuro || 0), 0),
            0,
          ).toFixed(2),
        ),
        receivedPaymentsEuro: Number(
          sortedContracts.reduce(
            (sum, contract) =>
              sum + contract.paidPayments.reduce((eventSum, event) => eventSum + Number(event.amountEuro || 0), 0),
            0,
          ).toFixed(2),
        ),
        landPaymentsEuro: Number(
          sumLandPaymentsByStatus(sortedContracts, LAND_PAYMENT_STATUS_REALIZED).toFixed(2),
        ),
        landPaymentsPlannedEuro: sumLandPaymentsByStatus(sortedContracts, LAND_PAYMENT_STATUS_PLANNED),
        landPaymentsRealizedEuro: sumLandPaymentsByStatus(sortedContracts, LAND_PAYMENT_STATUS_REALIZED),
        landPaymentsCertifiedEuro: sumLandPaymentsByStatus(sortedContracts, LAND_PAYMENT_STATUS_CERTIFIED),
        contracts: sortedContracts,
        years,
      } satisfies ProjectNode;
    })
    .sort((a, b) => a.name.localeCompare(b.name, "fr"));
}

export function buildProjectReminders(project: ProjectNode | null): SynthesesReminderItem[] {
  if (!project) return [];

  const reminders: SynthesesReminderItem[] = [];
  const projectTargetNodeId = `project-${project.id}`;

  if (project.contracts.length === 0) {
    reminders.push({
      id: `project-${project.id}-empty`,
      level: "medium",
      title: "Aucun contrat",
      description: "Ce projet n'a pas encore de contrat rattache.",
      targetNodeId: projectTargetNodeId,
    });
    return reminders;
  }

  const missingDateContracts = project.contracts.filter(
    (contract) => !contract.contractDate && !contract.isVerdeo,
  );
  if (missingDateContracts.length > 0) {
    reminders.push({
      id: `project-${project.id}-missing-date`,
      level: "medium",
      title: "Date du contrat a renseigner",
      description: `${missingDateContracts.length} contrat(s) sans vraie date de contrat.`,
      targetNodeId: projectTargetNodeId,
    });
  }

  const fallbackDateContracts = project.contracts.filter((contract) => contract.dateUsesFallback);
  if (fallbackDateContracts.length > 0) {
    reminders.push({
      id: `project-${project.id}-fallback-date`,
      level: "low",
      title: "Date de reference utilisee",
      description:
        "Une date de reference est utilisee sur certains contrats pour figer le taux de change.",
      targetNodeId: projectTargetNodeId,
    });
  }

  const today = new Date();
  const overdueExpectedPayments = project.contracts.flatMap((contract) =>
    contract.expectedPayments.filter((event) => {
      if (!event.date || event.datePaid) return false;
      const date = new Date(event.date);
      return !Number.isNaN(date.getTime()) && date < today;
    }),
  );
  if (overdueExpectedPayments.length > 0) {
    reminders.push({
      id: `project-${project.id}-overdue-expected`,
      level: "medium",
      title: "Versements prevus a relancer",
      description: `${overdueExpectedPayments.length} versement(s) prevu(s) sont deja echus.`,
      targetNodeId: projectTargetNodeId,
    });
  }

  return reminders.slice(0, 20);
}

export function buildProjectCalendarEvents(project: ProjectNode | null): SynthesesCalendarEvent[] {
  if (!project) return [];

  const events: SynthesesCalendarEvent[] = [];
  project.contracts.forEach((contract, index) => {
    if (contract.contractDate) {
      events.push({
        id: `${contract.id}-contract`,
        date: contract.contractDate,
        label: `${contract.title} • Contrat signe`,
        count: 1,
      });
    }

    contract.expectedPayments.forEach((event, eventIndex) => {
      if (!event.date) return;
      events.push({
        id: `${contract.id}-expected-${eventIndex}`,
        date: event.date,
        label: `${contract.title} • Versement prevu`,
        count: 1,
      });
    });

    contract.paidPayments.forEach((event, eventIndex) => {
      if (!event.date) return;
      events.push({
        id: `${contract.id}-paid-${eventIndex}`,
        date: event.date,
        label: `${contract.title} • Versement recu`,
        count: 1,
      });
    });

    contract.landPayments.forEach((event, eventIndex) => {
      if (!event.date) return;
      const status = getLandPaymentStatus(event.status);
      events.push({
        id: `${contract.id}-land-${eventIndex}`,
        date: event.date,
        label: `${contract.title} • ${getLandPaymentStatusLabel(status)}`,
        count: 1,
      });
    });

    void index;
  });

  return events;
}

function StatCard({
  icon,
  label,
  value,
  loading = false,
  tone = "slate",
}: {
  icon: ReactNode;
  label: string;
  value: string;
  loading?: boolean;
  tone?: SynthesesMetricTone;
}) {
  const toneClasses = getSynthesesMetricToneClasses(tone);
  return (
    <div
      className={`rounded-xl border p-3 shadow-[0_1px_0_rgba(15,23,42,0.03)] ${toneClasses.card}`}
    >
      <div className="mb-2 flex items-center gap-2">
        <span
          className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ring-1 ring-black/5 ${toneClasses.iconWrap}`}
        >
          {icon}
        </span>
        <span className={`text-xs uppercase tracking-wide ${toneClasses.label}`}>
          {label}
        </span>
      </div>
      {loading ? (
        <Skeleton className="h-7 w-24" />
      ) : (
        <div className={`text-xl font-semibold ${toneClasses.value}`}>
          {value}
        </div>
      )}
    </div>
  );
}

function ProjectsListSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 5 }).map((_, index) => (
        <div key={index} className="rounded-xl border border-slate-200 p-3">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="mt-2 h-3 w-24" />
        </div>
      ))}
    </div>
  );
}

function buildMonthlyValues(
  yearSummary: ProjectYearSummary,
  key: keyof ProjectMonthlyBucket,
) {
  return MONTH_COLUMNS.map((month) =>
    Number((yearSummary.months[month.index]?.[key] || 0).toFixed(2)),
  );
}

function CustomHorizontalScroller({
  children,
}: {
  children: ReactNode;
}) {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef<{
    pointerId: number;
    startClientX: number;
    startScrollLeft: number;
  } | null>(null);
  const [metrics, setMetrics] = useState({
    viewportWidth: 0,
    contentWidth: 0,
    maxScrollLeft: 0,
  });
  const [scrollLeft, setScrollLeft] = useState(0);

  useEffect(() => {
    const viewportNode = viewportRef.current;
    const contentNode = contentRef.current;
    if (!viewportNode || !contentNode) return;

    const updateMetrics = () => {
      const viewportWidth = viewportNode.clientWidth;
      const contentWidth = contentNode.scrollWidth;
      const maxScrollLeft = Math.max(0, contentWidth - viewportWidth);

      setMetrics({
        viewportWidth,
        contentWidth,
        maxScrollLeft,
      });
      setScrollLeft((current) => Math.min(current, maxScrollLeft));
    };

    updateMetrics();

    const observer = new ResizeObserver(() => {
      updateMetrics();
    });
    observer.observe(viewportNode);
    observer.observe(contentNode);

    return () => {
      observer.disconnect();
    };
  }, [children]);

  const syncScrollLeft = (nextScrollLeft: number) => {
    const viewportNode = viewportRef.current;
    const clamped = Math.max(0, Math.min(nextScrollLeft, metrics.maxScrollLeft));
    if (viewportNode) viewportNode.scrollLeft = clamped;
    setScrollLeft(clamped);
  };

  const handleViewportScroll = () => {
    const viewportNode = viewportRef.current;
    if (!viewportNode) return;
    setScrollLeft(viewportNode.scrollLeft);
  };

  const handleTrackPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    const trackNode = trackRef.current;
    if (!trackNode || metrics.maxScrollLeft <= 0) return;

    const bounds = trackNode.getBoundingClientRect();
    const ratio = Math.max(
      0,
      Math.min(1, (event.clientX - bounds.left) / bounds.width),
    );
    syncScrollLeft(ratio * metrics.maxScrollLeft);
  };

  const handleThumbPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (metrics.maxScrollLeft <= 0) return;
    dragStateRef.current = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startScrollLeft: scrollLeft,
    };

    const releaseDrag = () => {
      dragStateRef.current = null;
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", releaseDrag);
      window.removeEventListener("pointercancel", releaseDrag);
    };

    const handlePointerMove = (moveEvent: PointerEvent) => {
      const state = dragStateRef.current;
      const trackNode = trackRef.current;
      if (!state || !trackNode) return;
      if (moveEvent.pointerId !== state.pointerId) return;

      const deltaX = moveEvent.clientX - state.startClientX;
      const trackWidth = trackNode.clientWidth;
      if (trackWidth <= 0) return;

      const nextScrollLeft =
        state.startScrollLeft +
        (deltaX / trackWidth) * metrics.maxScrollLeft;
      syncScrollLeft(nextScrollLeft);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", releaseDrag);
    window.addEventListener("pointercancel", releaseDrag);
  };

  const thumbWidthPercent =
    metrics.contentWidth > 0
      ? Math.max(
          12,
          Math.min(100, (metrics.viewportWidth / metrics.contentWidth) * 100),
        )
      : 100;
  const thumbTravelPercent = Math.max(0, 100 - thumbWidthPercent);
  const thumbLeftPercent =
    metrics.maxScrollLeft > 0
      ? (scrollLeft / metrics.maxScrollLeft) * thumbTravelPercent
      : 0;

  return (
    <div className="min-w-0 w-full max-w-full">
      <div
        ref={viewportRef}
        className="hide-native-scrollbar min-w-0 w-full max-w-full overflow-x-auto pb-2"
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
        onScroll={handleViewportScroll}
      >
        <div ref={contentRef} className="w-max">
          {children}
        </div>
      </div>

      {metrics.maxScrollLeft > 0 ? (
        <div className="px-1">
          <div
            ref={trackRef}
            className="relative h-3 w-full cursor-pointer rounded-full bg-slate-200/90"
            onPointerDown={handleTrackPointerDown}
          >
            <div
              role="slider"
              aria-label="Défilement horizontal de la vue mensuelle"
              aria-valuemin={0}
              aria-valuemax={Math.round(metrics.maxScrollLeft)}
              aria-valuenow={Math.round(scrollLeft)}
              tabIndex={0}
              onPointerDown={handleThumbPointerDown}
              className="absolute top-[2px] h-[8px] rounded-full bg-slate-400 shadow-sm transition-colors hover:bg-slate-500"
              style={{
                left: `${thumbLeftPercent}%`,
                width: `${thumbWidthPercent}%`,
              }}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function MonthlyTable({
  yearSummary,
}: {
  yearSummary: ProjectYearSummary | null;
}) {
  if (!yearSummary) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-6 text-sm text-slate-500">
        Aucune annee disponible pour ce projet.
      </div>
    );
  }

  const contractArr = buildMonthlyValues(yearSummary, "contractAmountEuro");
  const expectedArr = buildMonthlyValues(yearSummary, "expectedPaymentsEuro");
  const receivedArr = buildMonthlyValues(yearSummary, "receivedPaymentsEuro");
  const landPlannedArr = buildMonthlyValues(yearSummary, "landPaymentsPlannedEuro");
  const landRealizedArr = buildMonthlyValues(yearSummary, "landPaymentsRealizedEuro");
  const landCertifiedArr = buildMonthlyValues(yearSummary, "landPaymentsCertifiedEuro");

  return (
    <CustomHorizontalScroller>
      <div className="min-w-[1120px] space-y-6">
        <div className="flex items-center gap-3">
          <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm ring-1 ring-slate-200">
            <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
            Vue globale
          </div>
          <div className="h-px flex-1 bg-slate-200" />
        </div>

        <div className="rounded-3xl bg-white px-4 py-4 ring-1 ring-slate-200">
          <div className="mb-3 flex items-center gap-3 w-full">
            <div className={cn(MONTH_LABEL_COLUMN_CLASS, "text-[11px] font-semibold uppercase tracking-wide text-slate-500")}>
              Mois
            </div>
            <div className={MONTH_CELLS_ROW_CLASS}>
              {MONTH_COLUMNS.map((month) => (
                <div
                  key={`month-header-${month.index}`}
                  className="rounded-md border border-slate-200 bg-slate-50 px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-600"
                >
                  {month.label}
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3 w-full">
            <div className={cn(MONTH_LABEL_COLUMN_CLASS, "text-sm font-medium text-[#586071]")}>
              Contrats signés
            </div>

            <div className={MONTH_CELLS_ROW_CLASS}>
              {contractArr.map((value, index) => (
                <FieldInput
                  key={`project-contract-${yearSummary.year}-${index}`}
                  type={2}
                  defaultValue={value || 0}
                  valueMultiple={String(value || 0)}
                  data={{ idFinanceur: 0, year: Number(yearSummary.year) || 0, month: index, idDevise: 0 }}
                />
              ))}
            </div>
          </div>

          <div className="mt-2 text-[11px] text-muted-foreground">
            Devise affichée : €
          </div>
        </div>

        <div className="rounded-3xl bg-[#F3FAF3] px-4 py-4">
          <div className="flex items-center gap-3 mb-3 w-full">
            <div className={cn(MONTH_LABEL_COLUMN_CLASS, "text-sm font-medium text-[#586071]")}>
              Versements prévus
            </div>
            <div className={MONTH_CELLS_ROW_CLASS}>
              {expectedArr.map((value, index) => (
                <FieldInput
                  key={`project-expected-${yearSummary.year}-${index}`}
                  type={5}
                  defaultValue={value || 0}
                  valueMultiple={String(value || 0)}
                  data={{ idFinanceur: 0, year: Number(yearSummary.year) || 0, month: index, idDevise: 0 }}
                />
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3 w-full">
            <div className={cn(MONTH_LABEL_COLUMN_CLASS, "text-sm font-medium text-[#586071]")}>
              Versements reçus
            </div>
            <div className={MONTH_CELLS_ROW_CLASS}>
              {receivedArr.map((value, index) => (
                <FieldInput
                  key={`project-received-${yearSummary.year}-${index}`}
                  type={6}
                  defaultValue={value || 0}
                  valueMultiple={String(value || 0)}
                  data={{ idFinanceur: 0, year: Number(yearSummary.year) || 0, month: index, idDevise: 0 }}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-3xl bg-[#F7FFF5] px-4 py-4">
          <div className="flex items-center gap-3 mb-3 w-full">
            <div className={cn(MONTH_LABEL_COLUMN_CLASS, "text-sm font-medium text-[#586071]")}>
              Versements terrain prévus
            </div>
            <div className={MONTH_CELLS_ROW_CLASS}>
              {landPlannedArr.map((value, index) => (
                <FieldInput
                  key={`project-land-planned-${yearSummary.year}-${index}`}
                  type={1}
                  defaultValue={value || 0}
                  valueMultiple={String(value || 0)}
                  data={{ idFinanceur: 0, year: Number(yearSummary.year) || 0, month: index, idDevise: 0 }}
                />
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3 mb-3 w-full">
            <div className={cn(MONTH_LABEL_COLUMN_CLASS, "text-sm font-medium text-[#586071]")}>
              Versements terrain réalisés
            </div>
            <div className={MONTH_CELLS_ROW_CLASS}>
              {landRealizedArr.map((value, index) => (
                <FieldInput
                  key={`project-land-realized-${yearSummary.year}-${index}`}
                  type={2}
                  defaultValue={value || 0}
                  valueMultiple={String(value || 0)}
                  data={{ idFinanceur: 0, year: Number(yearSummary.year) || 0, month: index, idDevise: 0 }}
                />
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3 w-full">
            <div className={cn(MONTH_LABEL_COLUMN_CLASS, "text-sm font-medium text-[#586071]")}>
              Versements terrain certifiés
            </div>
            <div className={MONTH_CELLS_ROW_CLASS}>
              {landCertifiedArr.map((value, index) => (
                <FieldInput
                  key={`project-land-certified-${yearSummary.year}-${index}`}
                  type={3}
                  defaultValue={value || 0}
                  valueMultiple={String(value || 0)}
                  data={{ idFinanceur: 0, year: Number(yearSummary.year) || 0, month: index, idDevise: 0 }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </CustomHorizontalScroller>
  );
}

function SynthesesProjetsBoard() {
  const location = useLocation();
  const navigate = useNavigate();
  const programsQuery = useFetchDataV2<ProgramResponse>(
    `${import.meta.env.VITE_API_ENDPOINT}/info/programs`,
    { browserCache: true, browserCacheNamespace: "syntheses" },
  );
  const groupedContractsQuery = useSynthesesGroupedContractsRead("financeurs");

  const projectCatalog = useMemo(
    () => buildProjectCatalog(programsQuery.data),
    [programsQuery.data],
  );
  const groupedFinanceurs = useMemo(
    () => (Array.isArray(groupedContractsQuery.data?.items) ? groupedContractsQuery.data.items : []),
    [groupedContractsQuery.data?.items],
  );
  const projects = useMemo(
    () => buildProjectsFromContracts(projectCatalog, groupedFinanceurs),
    [projectCatalog, groupedFinanceurs],
  );

  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [selectedYearByProjectId, setSelectedYearByProjectId] = useState<Record<number, string>>({});
  const [projectSearch, setProjectSearch] = useState("");
  const stickyHeaderRef = useRef<HTMLDivElement | null>(null);
  const [isStickyHeaderActive, setIsStickyHeaderActive] = useState(false);
  const handledRouteTargetRef = useRef<string>("");
  const routeTargetProject = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const projectId = Number(searchParams.get("targetProjectId") || 0);
    const projectName = toTrimmedText(searchParams.get("targetProjectName"));

    if (!(projectId > 0) && !projectName) {
      return null;
    }

    return {
      projectId: projectId > 0 ? projectId : null,
      projectName: projectName || null,
      key: `${projectId > 0 ? projectId : "none"}::${projectName || "none"}`,
    };
  }, [location.search]);

  const normalizedProjectSearch = useMemo(
    () => toTrimmedText(projectSearch).toLowerCase(),
    [projectSearch],
  );
  const filteredProjects = useMemo(() => {
    if (!normalizedProjectSearch) return projects;

    return projects.filter((project) => {
      const projectName = toTrimmedText(project.name).toLowerCase();
      const programName = toTrimmedText(project.programName).toLowerCase();
      return (
        projectName.includes(normalizedProjectSearch)
        || programName.includes(normalizedProjectSearch)
      );
    });
  }, [normalizedProjectSearch, projects]);

  useEffect(() => {
    if (!filteredProjects.length) {
      setSelectedProjectId(null);
      return;
    }

    setSelectedProjectId((current) => {
      if (current && filteredProjects.some((project) => project.id === current)) {
        return current;
      }
      return filteredProjects[0]?.id ?? null;
    });
  }, [filteredProjects]);

  useEffect(() => {
    if (!routeTargetProject || projects.length === 0) return;
    if (handledRouteTargetRef.current === routeTargetProject.key) return;

    const targetProject =
      projects.find((project) =>
        routeTargetProject.projectId
          ? project.id === routeTargetProject.projectId
          : toTrimmedText(project.name).toLowerCase()
              === toTrimmedText(routeTargetProject.projectName).toLowerCase(),
      ) || null;
    if (!targetProject) return;

    if (projectSearch) {
      setProjectSearch("");
    }

    if (selectedProjectId !== targetProject.id) {
      setSelectedProjectId(targetProject.id);
      return;
    }

    handledRouteTargetRef.current = routeTargetProject.key;
    scrollToTargetNode(`project-${targetProject.id}`);

    const nextSearchParams = new URLSearchParams(location.search);
    nextSearchParams.delete("targetProjectId");
    nextSearchParams.delete("targetProjectName");
    const nextSearch = nextSearchParams.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true },
    );
  }, [
    location.pathname,
    location.search,
    navigate,
    projectSearch,
    projects,
    routeTargetProject,
    selectedProjectId,
  ]);

  const selectedProject = filteredProjects.find((project) => project.id === selectedProjectId) ?? null;

  useEffect(() => {
    if (!selectedProject) return;
    setSelectedYearByProjectId((current) => {
      if (current[selectedProject.id]) return current;
      const latestYear = selectedProject.years[selectedProject.years.length - 1]?.year || "";
      if (!latestYear) return current;
      return {
        ...current,
        [selectedProject.id]: latestYear,
      };
    });
  }, [selectedProject]);

  useEffect(() => {
    const headerNode = stickyHeaderRef.current;
    if (!headerNode || typeof window === "undefined") return;

    const desktopMedia = window.matchMedia("(min-width: 1280px)");
    const scrollParent = getScrollParent(headerNode);

    const updateStickyState = () => {
      if (!desktopMedia.matches) {
        setIsStickyHeaderActive(false);
        return;
      }

      const headerRect = headerNode.getBoundingClientRect();
      if (scrollParent === window) {
        setIsStickyHeaderActive(headerRect.top <= 0.5 && window.scrollY > 0);
        return;
      }

      const scrollParentElement = scrollParent as HTMLElement;
      const parentRect = scrollParentElement.getBoundingClientRect();
      setIsStickyHeaderActive(
        headerRect.top <= parentRect.top + 0.5 && scrollParentElement.scrollTop > 0,
      );
    };

    updateStickyState();
    const scrollTarget: HTMLElement | Window = scrollParent;
    scrollTarget.addEventListener("scroll", updateStickyState, {
      passive: true,
    });
    window.addEventListener("resize", updateStickyState);

    const handleMediaChange = () => updateStickyState();
    if (typeof desktopMedia.addEventListener === "function") {
      desktopMedia.addEventListener("change", handleMediaChange);
    } else if (typeof desktopMedia.addListener === "function") {
      desktopMedia.addListener(handleMediaChange);
    }

    return () => {
      scrollTarget.removeEventListener("scroll", updateStickyState);
      window.removeEventListener("resize", updateStickyState);
      if (typeof desktopMedia.removeEventListener === "function") {
        desktopMedia.removeEventListener("change", handleMediaChange);
      } else if (typeof desktopMedia.removeListener === "function") {
        desktopMedia.removeListener(handleMediaChange);
      }
    };
  }, [selectedProjectId, projects.length]);

  const selectedYear = selectedProject
    ? selectedYearByProjectId[selectedProject.id] || selectedProject.years[selectedProject.years.length - 1]?.year || ""
    : "";
  const selectedYearSummary = selectedProject?.years.find((year) => year.year === selectedYear) ?? selectedProject?.years[0] ?? null;
  const loading = programsQuery.loading || groupedContractsQuery.loading;
  const isFetching = programsQuery.isFetching || groupedContractsQuery.isFetching;

  return (
    <TooltipProvider delayDuration={120}>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[280px_minmax(0,1fr)]">
        <aside className="rounded-xl border bg-white p-4 xl:sticky xl:top-6 xl:h-[calc(100vh-3rem)] xl:overflow-hidden">
          <div className="mb-3 text-xs uppercase tracking-wide text-slate-500">
            Projets
          </div>

          <label className="mb-3 block">
            <span className="sr-only">Rechercher un projet ou un programme</span>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                value={projectSearch}
                onChange={(event) => setProjectSearch(event.target.value)}
                placeholder="Rechercher un projet ou un programme"
                className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 pl-9 pr-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-300 focus:bg-white focus:ring-2 focus:ring-slate-200"
              />
            </div>
          </label>

          {loading || isFetching ? (
            <ProjectsListSkeleton />
          ) : projects.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
              Aucun projet trouve.
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
              Aucun projet ne correspond a votre recherche.
            </div>
          ) : (
            <div className="space-y-2 xl:max-h-[calc(100vh-11rem)] xl:overflow-y-auto xl:pr-1">
              {filteredProjects.map((project) => {
                const isActive = project.id === selectedProjectId;
                return (
                  <div
                    key={project.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setSelectedProjectId(project.id);
                      scrollToTargetNode(`project-${project.id}`);
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter" && event.key !== " ") return;
                      event.preventDefault();
                      setSelectedProjectId(project.id);
                      scrollToTargetNode(`project-${project.id}`);
                    }}
                    className={cn(
                      "w-full cursor-pointer rounded-xl border p-3 text-left transition focus:outline-none focus:ring-2 focus:ring-emerald-200",
                      isActive
                        ? "border-emerald-300 bg-emerald-50/70 shadow-sm"
                        : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50",
                    )}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-sm font-medium text-slate-900">{project.name}</div>
                      {getProjectDashboardHref(project.id) ? (
                        <a
                          href={getProjectDashboardHref(project.id) || undefined}
                          target="_blank"
                          rel="noreferrer noopener"
                          onClick={(event) => event.stopPropagation()}
                          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:border-slate-300 hover:text-slate-700"
                          title="Ouvrir le projet dans un nouvel onglet"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      ) : null}
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      {project.programName || "Programme non renseigne"}
                    </div>
                    <div className="mt-2 text-xs font-medium text-slate-700">
                      {formatEuro(project.totalAmountEuro)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </aside>

        <section className="min-w-0 space-y-4">
          {!selectedProject ? (
            <div className="rounded-xl border bg-white p-6 text-sm text-slate-500">
              Selectionnez un projet pour voir sa synthese.
            </div>
          ) : (
            <>
              <div
                ref={stickyHeaderRef}
                data-syntheses-sticky-header="true"
                className={cn(
                  "xl:sticky xl:top-0 xl:z-20 xl:bg-[#fff] xl:pt-6 xl:px-4 rounded-b-2xl",
                  isStickyHeaderActive
                    ? "xl:shadow-[0_18px_24px_-22px_rgba(15,23,42,0.4)]"
                    : "xl:shadow-none",
                )}
              >
                <div className="space-y-4 pb-4">
                  <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 bg-gradient-to-r from-emerald-50/90 via-white to-sky-50/60 px-4 py-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <h2 className="text-xl font-semibold text-slate-900">{selectedProject.name}</h2>
                          {getProjectDashboardHref(selectedProject.id) ? (
                            <a
                              href={getProjectDashboardHref(selectedProject.id) || undefined}
                              target="_blank"
                              rel="noreferrer noopener"
                              className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:border-slate-300 hover:text-slate-700"
                              title="Ouvrir le projet dans un nouvel onglet"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          ) : null}
                        </div>
                        <p className="mt-1 text-sm text-slate-500">
                          {selectedProject.programName || "Programme non renseigne"}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => scrollToTargetNode(`project-${selectedProject.id}`)}
                        className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600"
                      >
                        Voir le detail
                        <ChevronRight className="h-3.5 w-3.5" />
                      </button>
                    </div>

                    <div className="grid gap-3 p-4 md:grid-cols-4">
                      <StatCard
                        icon={<CalendarDays className="h-4 w-4" />}
                        label="Contrats"
                        value={String(selectedProject.contractCount)}
                        loading={loading || isFetching}
                        tone="blue"
                      />
                      <StatCard
                        icon={<FolderKanban className="h-4 w-4" />}
                        label="Financeurs"
                        value={String(selectedProject.funderCount)}
                        loading={loading || isFetching}
                        tone="slate"
                      />
                      <StatCard
                        icon={<BriefcaseBusiness className="h-4 w-4" />}
                        label="Montant total"
                        value={formatEuro(selectedProject.totalAmountEuro)}
                        loading={loading || isFetching}
                        tone="sand"
                      />
                      <StatCard
                        icon={<Wallet className="h-4 w-4" />}
                        label="Versements recus"
                        value={formatEuro(selectedProject.receivedPaymentsEuro)}
                        loading={loading || isFetching}
                        tone="violet"
                      />
                    </div>
                  </div>

                  {selectedProject.years.length > 0 ? (
                    <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white p-3">
                      <div className="flex min-w-max gap-2">
                        {selectedProject.years.map((year) => (
                          <button
                            key={year.year}
                            type="button"
                            onClick={() =>
                              setSelectedYearByProjectId((current) => ({
                                ...current,
                                [selectedProject.id]: year.year,
                              }))
                            }
                            className={cn(
                              "rounded-xl border px-4 py-2 text-sm font-medium transition",
                              selectedYear === year.year
                                ? "border-emerald-600 bg-emerald-600 text-white shadow-sm"
                                : "border-slate-200 bg-slate-50/80 text-slate-700 hover:border-slate-300 hover:bg-white",
                            )}
                          >
                            {year.year}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}

                </div>
              </div>

              {programsQuery.error ? (
                <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
                  Impossible de charger la liste des projets.
                </div>
              ) : null}
              {groupedContractsQuery.error ? (
                <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
                  Impossible de charger les contrats projet.
                </div>
              ) : null}

              <SynthesesProjectImpactIndicators projectId={selectedProject.id} />

              <div
                data-contract-node-id={`project-${selectedProject.id}`}
                className="space-y-4"
              >
                {selectedProject.contracts.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-6 text-sm text-slate-500">
                    Aucun contrat rattache a ce projet pour le moment.
                  </div>
                ) : (
                  <>
                    {selectedYearSummary ? (
                      <div className="overflow-hidden rounded-2xl border border-amber-100 bg-white">
                        <div className="border-b border-amber-100 bg-gradient-to-r from-amber-50/90 via-white to-white px-4 py-4">
                          <h3 className="text-sm font-semibold text-slate-900">
                            Synthese annuelle • {selectedYearSummary.year}
                          </h3>
                          <p className="mt-1 text-xs text-slate-500">
                            Meme systeme de cartes que les autres onglets, applique a l&apos;annee selectionnee.
                          </p>
                        </div>
                        <div className="grid gap-3 p-4 md:grid-cols-2 xl:grid-cols-6">
                          <StatCard
                            icon={<BriefcaseBusiness className="h-4 w-4" />}
                            label="Contrats signes"
                            value={formatEuro(selectedYearSummary.contractAmountEuro)}
                            tone="blue"
                          />
                          <StatCard
                            icon={<CalendarDays className="h-4 w-4" />}
                            label="Versements prevus"
                            value={formatEuro(selectedYearSummary.expectedPaymentsEuro)}
                            tone="teal"
                          />
                          <StatCard
                            icon={<Wallet className="h-4 w-4" />}
                            label="Versements recus"
                            value={formatEuro(selectedYearSummary.receivedPaymentsEuro)}
                            tone="violet"
                          />
                          <StatCard
                            icon={<FolderKanban className="h-4 w-4" />}
                            label="Versements terrain prévus"
                            value={formatEuro(selectedYearSummary.landPaymentsPlannedEuro)}
                            tone="blue"
                          />
                          <StatCard
                            icon={<FolderKanban className="h-4 w-4" />}
                            label="Versements terrain réalisés"
                            value={formatEuro(selectedYearSummary.landPaymentsRealizedEuro)}
                            tone="olive"
                          />
                          <StatCard
                            icon={<FolderKanban className="h-4 w-4" />}
                            label="Versements terrain certifiés"
                            value={formatEuro(selectedYearSummary.landPaymentsCertifiedEuro)}
                            tone="rose"
                          />
                        </div>
                      </div>
                    ) : null}

                    <div className="overflow-hidden rounded-2xl border border-sky-100 bg-white">
                      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-sky-100 bg-gradient-to-r from-sky-50/90 via-white to-white px-4 py-4">
                        <div>
                          <h3 className="text-sm font-semibold text-slate-900">
                            Vue mensuelle {selectedYearSummary ? `• ${selectedYearSummary.year}` : ""}
                          </h3>
                          <p className="mt-1 text-xs text-slate-500">
                            Les contrats signes sont positionnes sur le mois de la date du contrat quand cette date tombe dans l&apos;annee selectionnee.
                          </p>
                        </div>
                      </div>
                      <div className="p-4">
                        <MonthlyTable yearSummary={selectedYearSummary} />
                      </div>
                    </div>

                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                      <div className="border-b border-slate-200 bg-slate-50/80 px-4 py-4">
                        <h3 className="text-sm font-semibold text-slate-900">
                          Detail des contrats
                        </h3>
                        <p className="mt-1 text-xs text-slate-500">
                          Montants affiches pour ce projet uniquement.
                        </p>
                      </div>

                      <div className="space-y-3 p-4">
                        {selectedProject.contracts.map((contract) => (
                          <div
                            key={contract.id}
                            data-contract-node-id={`contract-${selectedProject.id}-${contract.id}`}
                            className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50/40"
                          >
                            <div className="border-b border-slate-200 bg-white px-4 py-3">
                              <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-900">
                                <span>{contract.title}</span>
                                {contract.isVerdeo ? (
                                  <>
                                    <span className="rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-700">
                                      Lecture seule
                                    </span>
                                    <span className="rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                                      Via Verdeo
                                    </span>
                                  </>
                                ) : null}
                              </div>
                              <div className="mt-1 text-xs text-slate-500">
                                {Object.keys(contract.yearAllocations)
                                  .sort((a, b) => Number(a) - Number(b))
                                  .join(", ")}
                              </div>
                            </div>

                            <div className="grid gap-3 px-4 py-4 md:grid-cols-2 xl:grid-cols-4">
                              <div className="space-y-1">
                                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                  Financeur
                                </div>
                                <div className="text-sm text-slate-800">
                                  {contract.financeurName}
                                </div>
                              </div>

                              <div className="space-y-1">
                                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                  Date du contrat
                                </div>
                                <div className="inline-flex items-center gap-2 text-sm text-slate-800">
                                  <span>{formatDate(contract.contractDate)}</span>
                                  {contract.dateUsesFallback ? (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                                          <AlertTriangle className="h-3.5 w-3.5" />
                                        </span>
                                      </TooltipTrigger>
                                      <TooltipContent className="max-w-xs text-left text-xs">
                                        La vraie date du contrat n&apos;est pas encore renseignee. Une date de reference est utilisee pour figer le taux de change.
                                      </TooltipContent>
                                    </Tooltip>
                                  ) : null}
                                </div>
                              </div>

                              <div className="space-y-1 xl:text-right">
                                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                  Montant projet
                                </div>
                                <div className="text-sm font-semibold text-slate-900">
                                  {formatEuro(contract.projectAmountEuro)}
                                </div>
                              </div>

                              <div className="space-y-1 xl:text-right">
                                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                  Versements recus
                                </div>
                                <div className="text-sm font-semibold text-slate-900">
                                  {formatEuro(
                                    contract.paidPayments.reduce(
                                      (sum, event) => sum + Number(event.amountEuro || 0),
                                      0,
                                    ),
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </section>
      </div>
    </TooltipProvider>
  );
}

export default SynthesesProjetsBoard;
